import React, { useState, useEffect } from "react";
import { v4 as uuidv4 } from "uuid"; // Import UUID
import Swal from "sweetalert2"; // Import SweetAlert2
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Modal,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const UpdateConnectedAccount = ({ isSidebarOpen }) => {
  const [loading, setLoading] = useState(false); // For tracking API request state
  const [open, setOpen] = useState(false);
  const [updateMode, setUpdateMode] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [userPlan, setUserPlan] = useState("Basic"); // Default: Basic plan
  const [connectedAccounts, setconnectedAccounts] = useState([]); // Tracks connected accounts
  const [organizations, setOrganizations] = useState([]); // Tracks organizations
  const [expandedOrgs, setExpandedOrgs] = useState({}); // Track expanded organizations
  const [testingConnectivity, setTestingConnectivity] = useState({}); // Track testing state per account
  const [lastTested, setLastTested] = useState({}); // Track last test timestamp per account
  const [isOrgMode, setIsOrgMode] = useState(false); // Track if connecting organization
  const [showConnectionModal, setShowConnectionModal] = useState(false); // Control connection type modal
  const [searchQuery, setSearchQuery] = useState(''); // Search filter
  const [editingOrgAlias, setEditingOrgAlias] = useState(null); // Track which org alias is being edited
  const [orgAliasValue, setOrgAliasValue] = useState(''); // Temp value for org alias
  const navigate = useNavigate();

  const accountData = [
    {
      id: 1,
      name: "AWS",
      description: "Amazon Web Services",
      logo: "https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg",
    },
    {
      id: 2,
      name: "GCP",
      description: "Google Cloud Services",
      logo: "https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg",
    },

    {
      id: 3,
      name: "Azure",
      description: "Microsoft Cloud",
      logo: "https://upload.wikimedia.org/wikipedia/commons/a/a8/Microsoft_Azure_Logo.svg",
    },
  ];
  const loadConnectedAccounts = async () => {
    try {
      const tenantID = localStorage.getItem("tenantID");
      const tenantApiUrl = process.env.REACT_APP_TENANT_API;
      
      if (!tenantID) return;
      
      setLoading(true);
      
      const response = await fetch(`${tenantApiUrl}/v2/tenant/settings?tenantID=${tenantID}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch user data");
      }

      const userData = await response.json();
      
      if (userData.cloudAccounts) {
        const cloudAccountsData = typeof userData.cloudAccounts === 'string' 
          ? JSON.parse(userData.cloudAccounts) 
          : userData.cloudAccounts;
        let allAccounts = [];
        
        // Process single AWS accounts
        if (cloudAccountsData.aws && Array.isArray(cloudAccountsData.aws)) {
          const awsAccounts = cloudAccountsData.aws.map(account => {
            const arnParts = account.iamrole.split(":");
            const awsAccountId = arnParts.length >= 5 ? arnParts[4] : "Unknown";
            
            return {
              id: account.id || account.alias,
              name: "AWS",
              description: account.alias || account.id,
              logo: "https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg",
              iamrole: account.iamrole,
              awsAccountId: awsAccountId,
              alias: account.alias || account.id,
              type: 'single'
            };
          });
          allAccounts = [...allAccounts, ...awsAccounts];
        }
        
        // Process organization accounts
        if (cloudAccountsData.organizations && Array.isArray(cloudAccountsData.organizations)) {
          setOrganizations(cloudAccountsData.organizations);
        } else {
          setOrganizations([]);
        }
        
        setconnectedAccounts(allAccounts);
      } else {
        setconnectedAccounts([]);
        setOrganizations([]);
      }
    } catch (error) {
      console.error("Error loading accounts:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to load connected accounts. Please try again.",
        icon: "error",
      });
      setconnectedAccounts([]);
      setOrganizations([]);
    } finally {
      setLoading(false);
    }
  };


  const availableAccounts =
    userPlan === "Basic"
      ? accountData.slice(0, 1) // Only GCP for Basic plan
      : accountData; // All accounts for other plans

  const handleCardClick = (account) => {
    // Initialize selected account with empty id (alias) field
    setSelectedAccount({
      ...account,
      id: "", // Clear the id field for new accounts
      iamrole: "" // Clear the iamrole field for new accounts
    });

    // Open modal for connecting account
    setUpdateMode(false);
    setOpen(true);
  };


  const handleConnectAccount = async () => {
    if (!selectedAccount) {
      Swal.fire({
        title: "Error",
        text: "No account selected. Please select an account to connect.",
        icon: "error",
      });
      return;
    }

    setLoading(true);

    try {
      if (isOrgMode) {
        // Organization connection
        const tenantID = localStorage.getItem("tenantID");
        const orgApiDomain = process.env.REACT_APP_ORG_API || process.env.REACT_APP_API_DOMAIN;
        
        const response = await fetch(`${orgApiDomain}/connect-organization`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            tenantID,
            managementRoleArn: selectedAccount.iamrole,
            connectionType: "organization"
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to connect organization");
        }

        const data = await response.json();

        setOpen(false);
        
        await Swal.fire({
          title: "Organization Connected!",
          html: `
            <div style="text-align: left;">
              <p><strong>Organization ID:</strong> ${data.organizationId}</p>
              <p><strong>Management Account:</strong> ${data.managementAccountId}</p>
              <p><strong>Total Accounts:</strong> ${data.totalAccounts}</p>
              <p><strong>Connected:</strong> ${data.connectedAccounts}</p>
              <p><strong>Pending:</strong> ${data.pendingAccounts}</p>
              <hr/>
              <p style="color: #666; font-size: 0.9em;">
                • Organization connected successfully<br/>
                • Discovered ${data.totalAccounts} member accounts<br/>
                • ${data.connectedAccounts} accounts ready, ${data.pendingAccounts} need role deployment
              </p>
            </div>
          `,
          icon: "success",
          confirmButtonText: "View Accounts",
        });

        await loadConnectedAccounts();
        return;
      }

      // Single account connection (existing logic)
      const accountAlias = selectedAccount.id || "AWS Account";

      const newAccount = {
        id: accountAlias,
        iamrole: selectedAccount.iamrole,
        alias: accountAlias
      };

      // Prepare API request
      const tenantID = localStorage.getItem("tenantID");
      const apiDomain = process.env.REACT_APP_API_DOMAIN;
      const tenantApiUrl = process.env.REACT_APP_TENANT_API;

      // Fetch existing cloudAccounts from the API
      const existingResponse = await fetch(`${tenantApiUrl}/v2/tenant/settings?tenantID=${tenantID}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!existingResponse.ok) {
        throw new Error("Failed to fetch existing user data from the API.");
      }

      const existingData = await existingResponse.json();
      const existingCloudAccounts = existingData.cloudAccounts
        ? (typeof existingData.cloudAccounts === 'string' ? JSON.parse(existingData.cloudAccounts) : existingData.cloudAccounts)
        : {};

      // Add the new account to the AWS accounts array
      if (!existingCloudAccounts.aws) {
        existingCloudAccounts.aws = [];
      }
      
      if (!Array.isArray(existingCloudAccounts.aws)) {
        existingCloudAccounts.aws = [];
      }
      
      existingCloudAccounts.aws.push(newAccount);

      // Send the updated cloudAccounts to the database
      const response = await fetch(`${tenantApiUrl}/v2/tenant/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantID,
          cloudAccounts: existingCloudAccounts,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update cloud accounts in the database.");
      }

      const responseData = await response.json();

      // Extract AWS account ID from IAM role ARN
      const arnParts = selectedAccount.iamrole.split(":");
      const awsAccountId = arnParts.length >= 5 ? arnParts[4] : "Unknown";
      
      // Create a formatted account object for the local state
      const newFormattedAccount = {
        id: accountAlias, // Keep id for backward compatibility
        name: "AWS",
        description: accountAlias,
        logo: "https://upload.wikimedia.org/wikipedia/commons/9/93/Amazon_Web_Services_Logo.svg",
        iamrole: selectedAccount.iamrole,
        awsAccountId: awsAccountId,
        alias: accountAlias // Add alias field
      };
      
      setOpen(false);
      await loadConnectedAccounts();

      // Show success alert
      Swal.fire({
        title: "Connected!",
        text: `${selectedAccount.name} has been successfully connected.`,
        icon: "success",
      });
    } catch (error) {
      console.error("Error connecting account:", error);
      Swal.fire({
        title: "Error",
        text: error.message || "Failed to connect account. Please try again.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };



  const handleUpdateAccount = async () => {
    setLoading(true);

    if (!selectedAccount) {
      Swal.fire({
        title: "Error",
        text: "No account selected. Please select an account to update.",
        icon: "error",
      });
      setLoading(false);
      return;
    }

    try {
      const tenantID = localStorage.getItem("tenantID");
      const tenantApiUrl = process.env.REACT_APP_TENANT_API;

      // Fetch existing cloudAccounts
      const existingResponse = await fetch(`${tenantApiUrl}/v2/tenant/settings?tenantID=${tenantID}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!existingResponse.ok) throw new Error("Failed to fetch existing user data");

      const existingData = await existingResponse.json();
      const existingCloudAccounts = existingData.cloudAccounts
        ? (typeof existingData.cloudAccounts === 'string' ? JSON.parse(existingData.cloudAccounts) : existingData.cloudAccounts)
        : {};

      // Update the account in AWS accounts array
      if (existingCloudAccounts.aws && Array.isArray(existingCloudAccounts.aws)) {
        const accountIndex = existingCloudAccounts.aws.findIndex(
          account => account.id === selectedAccount.id
        );
        
        if (accountIndex !== -1) {
          existingCloudAccounts.aws[accountIndex] = {
            iamrole: selectedAccount.iamrole,
            alias: selectedAccount.id,
            id: selectedAccount.id
          };
        }
      }

      // Update the database
      const response = await fetch(`${tenantApiUrl}/v2/tenant/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantID,
          cloudAccounts: existingCloudAccounts,
        }),
      });

      if (!response.ok) throw new Error("Failed to update cloud accounts");

      setOpen(false);
      await loadConnectedAccounts();

      Swal.fire({
        title: "Updated!",
        text: `${selectedAccount.name} has been successfully updated.`,
        icon: "success",
      });
    } catch (error) {
      console.error("Error updating account:", error);
      Swal.fire({
        title: "Error",
        text: "Failed to update account. Please try again.",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };




  const handleDeleteAccount = async (account) => {
    Swal.fire({
      title: "Confirm Deletion",
      text: `Are you sure you want to delete AWS account ${account.awsAccountId}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          const tenantID = localStorage.getItem("tenantID");
          const tenantApiUrl = process.env.REACT_APP_TENANT_API;

          const existingResponse = await fetch(`${tenantApiUrl}/v2/tenant/settings?tenantID=${tenantID}`, {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          });

          if (!existingResponse.ok) throw new Error("Failed to fetch existing user data");

          const existingData = await existingResponse.json();
          let existingCloudAccounts = existingData.cloudAccounts
            ? (typeof existingData.cloudAccounts === 'string' ? JSON.parse(existingData.cloudAccounts) : existingData.cloudAccounts)
            : {};

          // Remove the account
          if (existingCloudAccounts.aws && Array.isArray(existingCloudAccounts.aws)) {
            existingCloudAccounts.aws = existingCloudAccounts.aws.filter(
              (acc) => {
                const matchesId = acc.id === account.id || acc.id === account.alias;
                const matchesAlias = acc.alias === account.id || acc.alias === account.alias;
                const matchesRole = acc.iamrole === account.iamrole;
                return !(matchesId || matchesAlias || matchesRole);
              }
            );
          }

          // Update the database
          const response = await fetch(`${tenantApiUrl}/v2/tenant/update`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              tenantID,
              cloudAccounts: existingCloudAccounts,
            }),
          });

          if (!response.ok) throw new Error("Failed to update cloud accounts");

          await Swal.fire({
            title: "Deleted!",
            text: `${account.name} has been successfully removed.`,
            icon: "success",
          });
          
          await loadConnectedAccounts();
        } catch (error) {
          console.error("Error deleting account:", error);
          Swal.fire({
            title: "Error",
            text: error.message || "Failed to delete the account. Please try again.",
            icon: "error",
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };



  const testConnectivity = async (accountId, roleArn) => {
    setTestingConnectivity(prev => ({ ...prev, [accountId]: 'testing' }));
    
    try {
      const tenantID = localStorage.getItem("tenantID");
      const apiDomain = process.env.REACT_APP_ORG_API;
      
      const response = await fetch(`${apiDomain}/test-connectivity`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tenantID, roleArn, accountId }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        const timestamp = new Date().toLocaleString();
        setTestingConnectivity(prev => ({ ...prev, [accountId]: 'success' }));
        setLastTested(prev => ({ ...prev, [accountId]: timestamp }));
        Swal.fire({
          title: "Connected!",
          html: `
            <div style="text-align: left;">
              <p><strong>Account:</strong> ${accountId}</p>
              <p style="color: #4caf50;">✓ SecureGuard: ${data.checks.secureGuard ? 'OK' : 'Failed'}</p>
              <p style="color: #4caf50;">✓ CloudMap: ${data.checks.cloudMap ? 'OK' : 'Failed'}</p>
              <p style="color: #4caf50;">✓ FinOps: ${data.checks.finOps ? 'OK' : 'Failed'}</p>
            </div>
          `,
          icon: "success",
        });
      } else {
        setTestingConnectivity(prev => ({ ...prev, [accountId]: 'failed' }));
        Swal.fire({
          title: "Connection Failed",
          text: data.error || "Unable to validate connectivity",
          icon: "error",
        });
      }
    } catch (error) {
      setTestingConnectivity(prev => ({ ...prev, [accountId]: 'failed' }));
      Swal.fire({
        title: "Error",
        text: "Failed to test connectivity",
        icon: "error",
      });
    }
  };

  const refreshOrganization = async (org) => {
    try {
      const tenantID = localStorage.getItem("tenantID");
      const orgApiDomain = process.env.REACT_APP_ORG_API || process.env.REACT_APP_API_DOMAIN;
      
      Swal.fire({
        title: 'Refreshing Organization',
        text: 'Re-validating member account connectivity...',
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading()
      });
      
      const response = await fetch(`${orgApiDomain}/connect-organization`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tenantID,
          managementRoleArn: org.managementRoleArn,
          orgAlias: org.alias || '',
          connectionType: "organization"
        }),
      });
      
      if (!response.ok) throw new Error("Failed to refresh organization");
      
      const data = await response.json();
      
      await loadConnectedAccounts();
      
      Swal.fire({
        title: "Organization Refreshed!",
        html: `
          <div style="text-align: left;">
            <p><strong>Organization:</strong> ${data.organizationId}</p>
            <p><strong>Connected:</strong> ${data.connectedAccounts}</p>
            <p><strong>Pending:</strong> ${data.pendingAccounts}</p>
          </div>
        `,
        icon: "success",
      });
    } catch (error) {
      Swal.fire('Error', error.message || 'Failed to refresh organization', 'error');
    }
  };

  const updateOrgAlias = async (org, newAlias) => {
    try {
      const organizationID = localStorage.getItem("organizationID");
      const apiDomain = process.env.REACT_APP_API_DOMAIN;
      
      const response = await fetch(`${apiDomain}/getUser`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ organizationID }),
      });
      
      const userData = await response.json();
      const cloudAccounts = JSON.parse(userData.cloudAccounts);
      
      const orgIndex = cloudAccounts.organizations.findIndex(o => o.orgId === org.orgId);
      if (orgIndex !== -1) {
        cloudAccounts.organizations[orgIndex].alias = newAlias;
        
        await fetch(`${apiDomain}/updateUser`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            organizationID,
            cloudAccounts: JSON.stringify(cloudAccounts)
          }),
        });
        
        await loadConnectedAccounts();
        setEditingOrgAlias(null);
      }
    } catch (error) {
      Swal.fire('Error', 'Failed to update organization name', 'error');
    }
  };

  const testAllConnectivity = async () => {
    const accountsToTest = [];
    organizations.forEach(org => {
      org.memberAccounts?.forEach(member => {
        if (member.connected) {
          accountsToTest.push({ 
            accountId: member.accountId, 
            roleArn: member.memberRoleArn,
            name: member.accountName 
          });
        }
      });
    });
    
    if (accountsToTest.length === 0) {
      Swal.fire('No Accounts', 'No connected accounts to test', 'info');
      return;
    }
    
    let successCount = 0;
    let failCount = 0;
    let currentIndex = 0;
    
    const updateModal = (current, total, status, accountName) => {
      const progress = Math.round((current / total) * 100);
      return `
        <div style="text-align: left;">
          <div style="margin-bottom: 20px;">
            <div style="background: #e0e0e0; border-radius: 10px; height: 20px; overflow: hidden;">
              <div style="background: linear-gradient(90deg, #342ead, #5a52d5); width: ${progress}%; height: 100%; transition: width 0.3s;"></div>
            </div>
            <p style="text-align: center; margin-top: 8px; color: #666; font-weight: bold;">${current} / ${total} accounts tested</p>
          </div>
          <div style="margin-bottom: 15px;">
            <p style="color: #666; margin: 5px 0;"><strong>Testing:</strong></p>
            <p style="color: #342ead; margin: 5px 0; font-size: 0.95em;">${accountName}</p>
          </div>
          <div style="background: #f5f5f5; padding: 15px; border-radius: 8px;">
            <p style="margin: 5px 0; color: #4caf50;">✓ SecureGuard ${status === 'testing' ? '(checking...)' : ''}</p>
            <p style="margin: 5px 0; color: #4caf50;">✓ CloudMap ${status === 'testing' ? '(checking...)' : ''}</p>
            <p style="margin: 5px 0; color: #4caf50;">✓ FinOps ${status === 'testing' ? '(checking...)' : ''}</p>
          </div>
          <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e0e0e0;">
            <p style="margin: 5px 0; color: #4caf50;">✓ Success: ${successCount}</p>
            <p style="margin: 5px 0; color: #f44336;">✗ Failed: ${failCount}</p>
          </div>
        </div>
      `;
    };
    
    Swal.fire({
      title: 'Testing All Connectivity',
      html: updateModal(0, accountsToTest.length, 'starting', 'Initializing...'),
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading()
    });
    
    for (const acc of accountsToTest) {
      currentIndex++;
      Swal.update({
        html: updateModal(currentIndex, accountsToTest.length, 'testing', acc.name)
      });
      
      try {
        const tenantID = localStorage.getItem("tenantID");
        const apiDomain = process.env.REACT_APP_ORG_API;
        
        const response = await fetch(`${apiDomain}/test-connectivity`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ tenantID, roleArn: acc.roleArn, accountId: acc.accountId }),
        });
        
        const data = await response.json();
        
        if (response.ok && data.success) {
          successCount++;
          setTestingConnectivity(prev => ({ ...prev, [acc.accountId]: 'success' }));
        } else {
          failCount++;
          setTestingConnectivity(prev => ({ ...prev, [acc.accountId]: 'failed' }));
        }
      } catch (error) {
        failCount++;
        setTestingConnectivity(prev => ({ ...prev, [acc.accountId]: 'failed' }));
      }
      
      Swal.update({
        html: updateModal(currentIndex, accountsToTest.length, 'complete', acc.name)
      });
      
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    
    Swal.fire({
      title: 'Testing Complete!',
      html: `
        <div style="text-align: center;">
          <div style="font-size: 3em; margin: 20px 0;">${successCount === accountsToTest.length ? '🎉' : '📊'}</div>
          <p style="font-size: 1.2em; margin: 10px 0;"><strong>Results Summary</strong></p>
          <div style="display: flex; justify-content: center; gap: 30px; margin: 20px 0;">
            <div>
              <p style="font-size: 2em; color: #4caf50; margin: 0;">${successCount}</p>
              <p style="color: #666; margin: 5px 0;">Successful</p>
            </div>
            <div>
              <p style="font-size: 2em; color: #f44336; margin: 0;">${failCount}</p>
              <p style="color: #666; margin: 5px 0;">Failed</p>
            </div>
          </div>
          <p style="color: #666; margin-top: 20px;">Total accounts tested: ${accountsToTest.length}</p>
        </div>
      `,
      icon: successCount === accountsToTest.length ? 'success' : 'warning',
      confirmButtonText: 'Done'
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    // Fetch user's selected plan from localStorage
    const storedPlan = JSON.parse(localStorage.getItem("selectedPlan"));
    if (storedPlan && storedPlan.name) {
      setUserPlan(storedPlan.name);
    }
    
    // Load accounts directly from API
    loadConnectedAccounts();
  }, []);

  return (
    <div
      className="cloudmap-page container-fluid"
      style={{
        marginLeft: isSidebarOpen ? "0px" : "0px",
        transition: "margin-left 0.3s ease",
      }}
    >
      <Box
        sx={{
          pt: 8,
          backgroundColor: "#f9f9f9",
          minHeight: "100vh",
          color: "#333",
          position: "relative",
        }}
      >
        {/* Breadcrumbs */}
        <Box sx={{ px: 4, py: 2 }}>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Dashboard / <strong style={{ color: '#342ead' }}>Connected Accounts</strong>
          </Typography>
        </Box>

        {/* Page Header with Stats */}
        <Box sx={{ px: 4, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ color: "#342ead", fontWeight: 600 }}>
              Connected Accounts
            </Typography>
            <Button
              variant="contained"
              onClick={() => setShowConnectionModal(true)}
              sx={{ bgcolor: '#342ead', '&:hover': { bgcolor: '#1d1e5a' } }}
            >
              + Connect Account
            </Button>
          </Box>
          
          {/* Summary Cards */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Paper sx={{ flex: 1, p: 2, borderLeft: '4px solid #342ead' }}>
              <Typography variant="caption" sx={{ color: '#666' }}>Total Organizations</Typography>
              <Typography variant="h5" sx={{ color: '#342ead', fontWeight: 600 }}>{organizations.length}</Typography>
            </Paper>
            <Paper sx={{ flex: 1, p: 2, borderLeft: '4px solid #4caf50' }}>
              <Typography variant="caption" sx={{ color: '#666' }}>Connected Accounts</Typography>
              <Typography variant="h5" sx={{ color: '#4caf50', fontWeight: 600 }}>
                {organizations.reduce((sum, org) => sum + (org.memberAccounts?.filter(m => m.connected).length || 0) + 1, 0) + connectedAccounts.length}
              </Typography>
            </Paper>
            <Paper sx={{ flex: 1, p: 2, borderLeft: '4px solid #ff9800' }}>
              <Typography variant="caption" sx={{ color: '#666' }}>Pending Deployment</Typography>
              <Typography variant="h5" sx={{ color: '#ff9800', fontWeight: 600 }}>
                {organizations.reduce((sum, org) => sum + (org.memberAccounts?.filter(m => !m.connected).length || 0), 0)}
              </Typography>
            </Paper>
            <Paper sx={{ flex: 1, p: 2, borderLeft: '4px solid #666' }}>
              <Typography variant="caption" sx={{ color: '#666' }}>Your Plan</Typography>
              <Typography variant="h5" sx={{ color: '#666', fontWeight: 600 }}>{userPlan}</Typography>
            </Paper>
          </Box>
        </Box>

        {/* Connection Type Modal */}
        <Modal
          open={showConnectionModal}
          onClose={() => setShowConnectionModal(false)}
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <Box sx={{ width: '80%', maxWidth: '900px', bgcolor: 'white', p: 4, borderRadius: 2 }}>
            <Typography variant="h5" sx={{ mb: 3, color: '#342ead', fontWeight: 600 }}>
              How would you like to connect?
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", gap: 3, flexWrap: "wrap" }}>
            <Card
              sx={{
                width: 350,
                height: 240,
                boxShadow: 3,
                backgroundColor: "#fff",
                borderRadius: 3,
                textAlign: "center",
                cursor: "pointer",
                border: "2px solid transparent",
                transition: "all 0.3s ease-in-out",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: "0 12px 24px rgba(52, 42, 173, 0.2)",
                  borderColor: "#342ead",
                },
              }}
              onClick={() => {
                setShowConnectionModal(false);
                setIsOrgMode(false);
                handleCardClick(availableAccounts[0]);
              }}
            >
              <CardContent>
                <Typography variant="h6" sx={{ mb: 1, color: "#342ead", fontWeight: 600, mt: 2 }}>
                  Single AWS Account
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontSize: "0.9rem" }}>
                  Connect one AWS account at a time
                </Typography>
                <Box sx={{ textAlign: "left", px: 2 }}>
                  <Typography variant="caption" display="block" sx={{ color: "#666", mb: 0.5 }}>
                    ✓ Quick 5-minute setup
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ color: "#666", mb: 0.5 }}>
                    ✓ For standalone accounts
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ color: "#666" }}>
                    ✓ Manual CloudFormation deploy
                  </Typography>
                </Box>
              </CardContent>
            </Card>
            
            <Card
              sx={{
                width: 350,
                height: 240,
                boxShadow: "0 8px 16px rgba(52, 42, 173, 0.15)",
                background: "linear-gradient(135deg, #f0f7ff 0%, #e3f2fd 100%)",
                borderRadius: 3,
                textAlign: "center",
                cursor: "pointer",
                border: "2px solid #342ead",
                transition: "all 0.3s ease-in-out",
                position: "relative",
                overflow: "visible",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: "0 16px 32px rgba(52, 42, 173, 0.25)",
                },
              }}
              onClick={() => {
                setShowConnectionModal(false);
                setIsOrgMode(true);
                setSelectedAccount(availableAccounts[0]);
                setUpdateMode(false);
                setOpen(true);
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 1, mt: 2 }}>
                  <Typography variant="h6" sx={{ color: "#342ead", fontWeight: 600 }}>
                    AWS Organization
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      ml: 1,
                      px: 1,
                      py: 0.5,
                      bgcolor: "#342ead",
                      color: "white",
                      borderRadius: 1,
                      fontWeight: "bold",
                      fontSize: "0.65rem",
                    }}
                  >
                    RECOMMENDED
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontSize: "0.9rem" }}>
                  Connect all accounts in your organization
                </Typography>
                <Box sx={{ textAlign: "left", px: 2 }}>
                  <Typography variant="caption" display="block" sx={{ color: "#666", mb: 0.5 }}>
                    ✓ Auto-discover all accounts
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ color: "#666", mb: 0.5 }}>
                    ✓ Consolidated billing & cost
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ color: "#666" }}>
                    ✓ Optional bulk auto-deploy
                  </Typography>
                </Box>
              </CardContent>
            </Card>
            </Box>
          </Box>
        </Modal>

        {/* Table Section */}
        <Box
          sx={{
            width: "80%",
            p: 2,
            pb: 10,
          }}
          className="container mt-5"
        >
          {/* Search Bar */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              placeholder="Search accounts by name, ID, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ bgcolor: 'white' }}
            />
          </Box>
          
          {/* Organizations Tree View */}
          {organizations.length > 0 ? (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, color: "#342ead", fontWeight: 600 }}>
                AWS Organizations ({organizations.length})
              </Typography>
              {organizations.map((org) => {
                const totalAccounts = org.memberAccounts?.length || 0;
                const connectedCount = org.memberAccounts?.filter(m => m.connected).length || 0;
                const isExpanded = expandedOrgs[org.orgId];
                
                return (
                  <Paper key={org.orgId} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: '#f0f7ff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Box 
                        sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, cursor: 'pointer' }}
                        onClick={() => setExpandedOrgs(prev => ({ ...prev, [org.orgId]: !prev[org.orgId] }))}
                      >
                        <Box sx={{ 
                          width: '24px', 
                          height: '24px', 
                          borderRadius: '4px', 
                          bgcolor: '#342ead', 
                          color: 'white', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          fontSize: '18px',
                          fontWeight: 'bold'
                        }}>
                          {isExpanded ? '−' : '+'}
                        </Box>
                        <img src="/organization.svg" alt="org" style={{ width: '24px', height: '24px' }} />
                        {editingOrgAlias === org.orgId ? (
                          <TextField
                            size="small"
                            value={orgAliasValue}
                            onChange={(e) => setOrgAliasValue(e.target.value)}
                            onBlur={() => {
                              if (orgAliasValue.trim()) {
                                updateOrgAlias(org, orgAliasValue);
                              } else {
                                setEditingOrgAlias(null);
                              }
                            }}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter' && orgAliasValue.trim()) {
                                updateOrgAlias(org, orgAliasValue);
                              }
                            }}
                            autoFocus
                            sx={{ width: '250px' }}
                          />
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" sx={{ color: '#342ead', fontWeight: 600 }}>
                              {org.alias || `Organization ${org.orgId}`}
                            </Typography>
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingOrgAlias(org.orgId);
                                setOrgAliasValue(org.alias || '');
                              }}
                              sx={{
                                cursor: 'pointer',
                                color: '#666',
                                '&:hover': { color: '#342ead' },
                                display: 'flex',
                                alignItems: 'center'
                              }}
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                              </svg>
                            </Box>
                          </Box>
                        )}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Typography variant="caption" sx={{ px: 1.5, py: 0.5, bgcolor: '#4caf50', color: 'white', borderRadius: 1, fontWeight: 'bold' }}>
                            {connectedCount} Connected
                          </Typography>
                          <Typography variant="caption" sx={{ px: 1.5, py: 0.5, bgcolor: '#ff9800', color: 'white', borderRadius: 1, fontWeight: 'bold' }}>
                            {totalAccounts - connectedCount} Pending
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/>
                            </svg>
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            refreshOrganization(org);
                          }}
                          sx={{ 
                            borderColor: '#342ead', 
                            color: '#342ead',
                            fontSize: '0.75rem',
                            '&:hover': { bgcolor: '#f0f7ff' }
                          }}
                        >
                          Refresh
                        </Button>
                        <Typography variant="body2" sx={{ color: '#666' }}>
                          {totalAccounts + 1} accounts total
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Collapse in={isExpanded}>
                      <Box sx={{ p: 2 }}>
                        {/* Management Account */}
                        <Box sx={{ mb: 2, p: 2, bgcolor: '#fff3e0', borderRadius: 1, border: '1px solid #ffb74d' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <img src="/orgaccount.svg" alt="mgmt" style={{ width: '20px', height: '20px' }} />
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Management Account</Typography>
                            <Box sx={{ px: 1, py: 0.5, bgcolor: '#342ead', color: 'white', borderRadius: 1, fontSize: '0.7rem', fontWeight: 'bold' }}>
                              MGMT
                            </Box>
                          </Box>
                          <Typography variant="body2" sx={{ color: '#666', fontSize: '0.85rem' }}>
                            Account ID: {org.id}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#666', wordBreak: 'break-all', display: 'block', mt: 0.5 }}>
                            {org.managementRoleArn}
                          </Typography>
                        </Box>
                        
                        {/* Member Accounts */}
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: '#666' }}>
                          Member Accounts ({org.memberAccounts?.length || 0}):
                        </Typography>
                        {org.memberAccounts && org.memberAccounts.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {org.memberAccounts
                              .filter(member => 
                                !searchQuery || 
                                member.accountName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                member.accountId.includes(searchQuery) ||
                                member.email.toLowerCase().includes(searchQuery.toLowerCase())
                              )
                              .map((member) => (
                              <Box
                                key={member.accountId}
                                sx={{
                                  p: 1.5,
                                  bgcolor: member.connected ? '#e8f5e9' : '#fff3e0',
                                  borderRadius: 1,
                                  border: `1px solid ${member.connected ? '#4caf50' : '#ff9800'}`,
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}
                              >
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <img src="/orgaccount.svg" alt="member" style={{ width: '18px', height: '18px' }} />
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                      {member.accountName}
                                    </Typography>
                                    {testingConnectivity[member.accountId] === 'success' ? (
                                      <Box sx={{ px: 1, py: 0.5, bgcolor: '#4caf50', color: 'white', borderRadius: 1, fontSize: '0.7rem', fontWeight: 'bold' }}>
                                        ✓ CONNECTED
                                      </Box>
                                    ) : testingConnectivity[member.accountId] === 'failed' ? (
                                      <Box sx={{ px: 1, py: 0.5, bgcolor: '#f44336', color: 'white', borderRadius: 1, fontSize: '0.7rem', fontWeight: 'bold' }}>
                                        ✗ FAILED
                                      </Box>
                                    ) : member.connected ? (
                                      <Box sx={{ px: 1, py: 0.5, bgcolor: '#2196f3', color: 'white', borderRadius: 1, fontSize: '0.7rem', fontWeight: 'bold' }}>
                                        ROLE DEPLOYED
                                      </Box>
                                    ) : (
                                      <Box sx={{ px: 1, py: 0.5, bgcolor: '#ff9800', color: 'white', borderRadius: 1, fontSize: '0.7rem', fontWeight: 'bold' }}>
                                        PENDING
                                      </Box>
                                    )}
                                  </Box>
                                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                    Account ID: {member.accountId}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                                    {member.email}
                                  </Typography>
                                  {lastTested[member.accountId] && (
                                    <Typography variant="caption" sx={{ color: '#999', display: 'block', fontSize: '0.7rem', mt: 0.5 }}>
                                      Last tested: {lastTested[member.accountId]}
                                    </Typography>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  {!member.connected && (
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      href="https://console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/create/review?templateURL=https://cloudmates-public-assets.s3.us-east-1.amazonaws.com/OdirMemberAccountTemplate.yml&stackName=odir-member-role"
                                      target="_blank"
                                      sx={{ fontSize: '0.75rem', borderColor: '#342ead', color: '#342ead' }}
                                    >
                                      Deploy Role
                                    </Button>
                                  )}
                                  {member.connected && (
                                    <Button
                                      variant="contained"
                                      size="small"
                                      disabled={testingConnectivity[member.accountId] === 'testing'}
                                      onClick={() => testConnectivity(member.accountId, member.memberRoleArn)}
                                      sx={{ 
                                        fontSize: '0.75rem', 
                                        bgcolor: '#342ead',
                                        '&:hover': { bgcolor: '#1d1e5a' }
                                      }}
                                    >
                                      {testingConnectivity[member.accountId] === 'testing' ? 'Testing...' : 'Test Connectivity'}
                                    </Button>
                                  )}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: '#999', fontStyle: 'italic' }}>
                            No member accounts found
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Paper>
                );
              })}
            </Box>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center', mb: 4 }}>
              <Typography variant="h6" sx={{ color: '#999', mb: 1 }}>No Organizations Connected</Typography>
              <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>Connect your AWS Organization to manage multiple accounts</Typography>
              <Button variant="contained" onClick={() => setShowConnectionModal(true)} sx={{ bgcolor: '#342ead' }}>
                Connect Organization
              </Button>
            </Paper>
          )}
          
          {/* Single Accounts Table */}
          {connectedAccounts.length > 0 ? (
            <Box>
              <Typography variant="h6" sx={{ mb: 2, color: "#342ead", fontWeight: 600 }}>
                Single AWS Accounts ({connectedAccounts.length})
              </Typography>
          <TableContainer
            component={Paper}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              width: "100%",
              minWidth: "600px",
            }}
          >
            <Table
              sx={{
                tableLayout: "fixed",
                width: "100%",
              }}
            >
              <TableHead sx={{ backgroundColor: "#342ead" }}>
                <TableRow>
                  <TableCell sx={{ color: "#fff", width: "50%" }}>IAM Role</TableCell>
                  <TableCell sx={{ color: "#fff", width: "30%" }}>Account ID</TableCell>
                  <TableCell sx={{ color: "#fff", width: "40%" }}>Alias</TableCell>
                  <TableCell sx={{ color: "#fff", width: "20%" }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {connectedAccounts
                  .filter(account => 
                    !searchQuery || 
                    account.alias?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    account.awsAccountId?.includes(searchQuery) ||
                    account.iamrole?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((account) => (
                  <TableRow key={account.id} sx={{ bgcolor: account.type?.includes('organization') ? '#f0f7ff' : 'inherit' }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {account.type === 'organization-mgmt' && (
                          <Box sx={{ px: 1, py: 0.5, bgcolor: '#342ead', color: 'white', borderRadius: 1, fontSize: '0.7rem', fontWeight: 'bold' }}>
                            ORG
                          </Box>
                        )}
                        <span style={{ fontSize: '0.85em', wordBreak: 'break-all' }}>{account.iamrole}</span>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <img
                          src={account.logo}
                          alt={account.name}
                          style={{ width: "30px", height: "30px" }}
                        />
                        {account.awsAccountId}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {account.alias || account.id}
                      {account.type === 'organization-member' && !account.connected && (
                        <Typography variant="caption" display="block" sx={{ color: '#ff9800', mt: 0.5, fontWeight: 500 }}>
                          Role not deployed
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        sx={{ mr: 1 }}
                        disabled={testingConnectivity[account.id] === 'testing'}
                        onClick={() => testConnectivity(account.id, account.iamrole)}
                      >
                        {testingConnectivity[account.id] === 'testing' ? 'Testing...' : 'Test'}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => {
                          setUpdateMode(true);
                          setSelectedAccount(account);
                          setOpen(true);
                        }}
                      >
                        Update
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleDeleteAccount(account)}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
            </Box>
          ) : organizations.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: '#999', mb: 1 }}>No Accounts Connected</Typography>
              <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>Get started by connecting your first AWS account</Typography>
              <Button variant="contained" onClick={() => setShowConnectionModal(true)} sx={{ bgcolor: '#342ead' }}>
                Connect Account
              </Button>
            </Paper>
          ) : null}
        </Box>

        {/* Modal */}
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="modal-title"
          aria-describedby="modal-description"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(5px)",
          }}
        >
          <Box
            sx={{
              width: "80%",
              maxWidth: "800px",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 3,
              textAlign: "left",
              position: 'relative',
            }}
          >
            {/* Cross (Close) Button */}
            <Button
              onClick={handleClose}
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                minWidth: 0,
                p: 1.2,
                borderRadius: '50%',
                backgroundColor: '#f4f4f4',
                color: '#342ead',
                '&:hover': { backgroundColor: '#eee', color: '#d32f2f' }
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 0 24 24" width="20" fill="currentColor"><path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7a1 1 0 1 0-1.41 1.41L10.59 12l-4.89 4.89a1 1 0 1 0 1.41 1.41L12 13.41l4.89 4.89a1 1 0 0 0 1.41-1.41L13.41 12l4.89-4.89a1 1 0 0 0 0-1.4z"/></svg>
            </Button>
            <Typography id="modal-title" variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              {updateMode
                ? `Update Details for ${selectedAccount?.name}`
                : `Connect to ${selectedAccount?.name}`}
            </Typography>
            
            {!updateMode && selectedAccount?.name === "AWS" && (
              <Box sx={{ mb: 3, p: 3, bgcolor: "#f8fafc", borderRadius: 2, border: "1px solid #e2e8f0" }}>
                {isOrgMode ? (
                  <>
                    <Box sx={{ mb: 3, p: 2, bgcolor: "#fff3cd", borderRadius: 1, border: "1px solid #ffc107" }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold", color: "#856404", mb: 1 }}>
                        What Happens Next?
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ color: "#856404", lineHeight: 1.6 }}>
                        • ODIR will discover all accounts in your organization<br/>
                        • You'll see a list of all member accounts<br/>
                        • Deploy member template to each account (manual or auto)<br/>
                        • Start scanning across all accounts
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, fontWeight: "bold", color: "#342ead" }}>
                      Step 1: Deploy in Management/Payer Account
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                      <Button
                        variant="contained"
                        size="small"
                        href="https://console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/create/review?templateURL=https://cloudmates-public-assets.s3.us-east-1.amazonaws.com/OdirOrganizationManagementTemplate-StackSets.yml&stackName=odir-organization-mgmt"
                        target="_blank"
                        sx={{
                          backgroundColor: "#FF9900",
                          color: "white",
                          "&:hover": { backgroundColor: "#EC7211" },
                        }}
                      >
                        Launch Organization Stack
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const url = 'https://console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/create/review?templateURL=https://cloudmates-public-assets.s3.us-east-1.amazonaws.com/OdirOrganizationManagementTemplate-StackSets.yml&stackName=odir-organization-mgmt';
                          navigator.clipboard.writeText(url);
                          alert('CloudFormation URL copied to clipboard!');
                        }}
                        sx={{
                          borderColor: "#FF9900",
                          color: "#FF9900",
                          "&:hover": { borderColor: "#EC7211", color: "#EC7211" },
                        }}
                      >
                        Copy URL
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        href="https://cloudmates-public-assets.s3.us-east-1.amazonaws.com/OdirOrganizationManagementTemplate-StackSets.yml"
                        download="OdirOrganizationManagementTemplate-StackSets.yml"
                        sx={{
                          backgroundColor: "#342ead",
                          color: "white",
                          "&:hover": { backgroundColor: "#1d1e5a" },
                        }}
                      >
                        Download Template
                      </Button>
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mb: 2, color: "#64748b", lineHeight: 1.6 }}>
                      Deploy this in your AWS Organization's <strong>management/payer account</strong>.<br/>
                      • Enables automatic account discovery<br/>
                      • Provides consolidated billing access<br/>
                      • Optional: Auto-deploy to all member accounts
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "#342ead" }}>
                      Step 2: Copy OrganizationManagementRoleArn from CloudFormation Outputs
                    </Typography>
                  </>
                ) : (
                  <>
                    <Box sx={{ mb: 3, p: 2, bgcolor: "#e8f5e9", borderRadius: 1, border: "1px solid #4caf50" }}>
                      <Typography variant="body2" sx={{ fontWeight: "bold", color: "#2e7d32", mb: 1 }}>
                        What Happens Next?
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ color: "#2e7d32", lineHeight: 1.6 }}>
                        • CloudFormation creates a read-only IAM role<br/>
                        • ODIR can scan your account for security & resources<br/>
                        • No write permissions - completely safe<br/>
                        • Takes ~2 minutes to complete
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, fontWeight: "bold", color: "#342ead" }}>
                      Step 1: Create IAM Role in Your AWS Account
                    </Typography>
                    <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                      <Button
                        variant="contained"
                        size="small"
                        href="https://console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/create/review?templateURL=https://cloudmates-public-assets.s3.us-east-1.amazonaws.com/OdirCustomerOnboardingCloudFormation.yml&stackName=odir-integration"
                        target="_blank"
                        sx={{
                          backgroundColor: "#FF9900",
                          color: "white",
                          "&:hover": { backgroundColor: "#EC7211" },
                        }}
                      >
                        Launch CloudFormation Stack
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const url = 'https://console.aws.amazon.com/cloudformation/home?region=us-east-1#/stacks/create/review?templateURL=https://cloudmates-public-assets.s3.us-east-1.amazonaws.com/OdirCustomerOnboardingCloudFormation.yml&stackName=odir-integration';
                          navigator.clipboard.writeText(url);
                          alert('CloudFormation URL copied to clipboard!');
                        }}
                        sx={{
                          borderColor: "#FF9900",
                          color: "#FF9900",
                          "&:hover": { borderColor: "#EC7211", color: "#EC7211" },
                        }}
                      >
                        Copy URL
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        href="https://cloudmates-public-assets.s3.us-east-1.amazonaws.com/OdirCustomerOnboardingCloudFormation.yml"
                        download="OdirCustomerOnboardingCloudFormation.yml"
                        sx={{
                          backgroundColor: "#342ead",
                          color: "white",
                          "&:hover": { backgroundColor: "#1d1e5a" },
                        }}
                      >
                        Download Template
                      </Button>
                    </Box>
                    <Typography variant="caption" display="block" sx={{ mb: 2, color: "#64748b", lineHeight: 1.6 }}>
                      Creates <strong>OdirMemberAccountRole</strong> with read-only permissions<br/>
                      • SecurityAudit + ViewOnlyAccess policies<br/>
                      • No data modification capabilities
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: "bold", color: "#342ead" }}>
                      Step 2: Copy Role ARN from Outputs and paste below
                    </Typography>
                  </>
                )}
              </Box>
            )}
            
            <TextField
              label={selectedAccount?.name === "AWS" ? "IAM Role ARN" : "IAM Role"}
              placeholder={selectedAccount?.name === "AWS" ? "arn:aws:iam::123456789012:role/OdirScanReadOnlyRole" : ""}
              variant="outlined"
              fullWidth
              sx={{ mb: 3 }}
              value={selectedAccount?.iamrole || ""}
              onChange={(e) =>
                setSelectedAccount({
                  ...selectedAccount,
                  iamrole: e.target.value,
                })
              }
            />
            <TextField
              label="Account Alias"
              variant="outlined"
              fullWidth
              sx={{ mb: 3 }}
              value={selectedAccount?.alias || selectedAccount?.id || ""}
              onChange={(e) =>
                setSelectedAccount({
                  ...selectedAccount,
                  id: e.target.value, // Keep updating id for backward compatibility
                  alias: e.target.value, // Also update the alias field
                })
              }
              placeholder="Enter a friendly name for this account"
            />
            <Button
              variant="contained"
              disabled={!selectedAccount?.iamrole || loading}
              onClick={updateMode ? handleUpdateAccount : handleConnectAccount}
              sx={{
                backgroundColor: !selectedAccount?.iamrole || loading ? "#ccc" : "#342ead",
                color: "white",
                "&:hover": {
                  backgroundColor: !selectedAccount?.iamrole || loading ? "#ccc" : "#1d1e5a",
                },
              }}
            >
              {loading
                ? "Processing..."
                : updateMode
                  ? "Update"
                  : "Connect"}
            </Button>
          </Box>
        </Modal>
      </Box>
    </div>

  );
};

export default UpdateConnectedAccount;
